/**
 * LESSON DETAIL PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { Lesson } from '../types/course';
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon, PencilIcon, TrashIcon, SparklesIcon, QuestionMarkCircleIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import EditLessonForm from '../components/forms/EditLessonForm';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Screen, Section, Panel, Button, Badge, Input, Textarea, Select, Divider } from '../design/primitives';
import { Spinner, Alert } from '../components/ui';

interface LessonDetailPageProps {
  isEditMode?: boolean;
}

const LessonDetailPage: React.FC<LessonDetailPageProps> = ({ isEditMode = false }) => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(isEditMode);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [showSummaryPrompt, setShowSummaryPrompt] = useState(false);
  const [showQuizPrompt, setShowQuizPrompt] = useState(false);
  const [showSummaryConfirm, setShowSummaryConfirm] = useState(false);
  const [showQuizConfirm, setShowQuizConfirm] = useState(false);

  // Quiz interaction state
  const [quizAnswers, setQuizAnswers] = useState<{[key: string]: string}>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState<{correct: number, total: number} | null>(null);

  // Default prompts from backend
  const defaultSummaryPrompt = `Create a comprehensive summary of this lesson.

The summary should:
1. Capture main topics and key points
2. Highlight important concepts and definitions
3. Use clear, organized structure
4. Include bullet points for readability
5. Focus on valuable learning takeaways

Transcript:
{transcript}

Generate a well-structured summary:`;

  const defaultQuizPrompt = `Create an interactive quiz to test understanding of this lesson.

Include:
- 5-10 multiple choice questions with explanations
- 2-3 short answer questions
- Questions that test comprehension, not just memorization
- Mix of difficulty levels (easy, medium, hard)

Lesson content:
{transcript}

{summary_section}

Generate the quiz in JSON format for interactive use.`;

  const [summaryPrompt, setSummaryPrompt] = useState(defaultSummaryPrompt);
  const [quizPrompt, setQuizPrompt] = useState(defaultQuizPrompt);

  // Model selection
  const [availableModels, setAvailableModels] = useState<Array<{id: string, name: string, description: string, provider: string}>>([]);
  const [selectedModel, setSelectedModel] = useState(() => {
    return localStorage.getItem('selectedAIModel') || 'gpt-4o';
  });

  const fetchLesson = useCallback(async () => {
    if (!lessonId) return;

    try {
      setLoading(true);
      const response = await apiClient.get<Lesson>(`/api/lessons/${lessonId}/`);
      setLesson(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load lesson details');
      console.error('Error fetching lesson:', err);
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    fetchLesson();
  }, [fetchLesson]);

  // Fetch available models
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await apiClient.get('/api/lessons/available_models/');
        if (response.data.success) {
          setAvailableModels(response.data.models);
        }
      } catch (err) {
        console.error('Error fetching available models:', err);
        setAvailableModels([
          { id: 'gpt-4o', name: 'GPT-4o', description: 'Most capable', provider: 'openai' },
          { id: 'gpt-4o-mini', name: 'GPT-4o Mini', description: 'Fast and affordable', provider: 'openai' },
          { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', description: 'Previous flagship', provider: 'openai' }
        ]);
      }
    };
    fetchModels();
  }, []);

  // Save selected model to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('selectedAIModel', selectedModel);
  }, [selectedModel]);

  const handleMarkCompleted = async () => {
    try {
      await apiClient.patch(`/api/lessons/${lessonId}/`, {
        is_completed: !lesson?.is_completed
      });
      setLesson(prev => prev ? { ...prev, is_completed: !prev.is_completed } : null);
    } catch (err) {
      console.error('Error updating lesson completion status:', err);
    }
  };

  const handleEdit = () => {
    setIsEditFormOpen(true);
  };

  const handleLessonUpdated = async () => {
    await fetchLesson();
    setIsEditFormOpen(false);
    if (isEditMode) {
      navigate(`/lessons/${lessonId}`);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/lessons/${lessonId}/`);
      navigate(-1);
    } catch (err) {
      console.error('Error deleting lesson:', err);
      setError('Failed to delete lesson');
    }
  };

  const handleGenerateSummary = async () => {
    if (!lesson?.transcript || !lesson.transcript.trim()) {
      setGenerateError('Cannot generate summary: lesson has no transcript. Please transcribe the lesson first.');
      return;
    }

    setShowSummaryConfirm(true);
  };

  const confirmGenerateSummary = async () => {
    setShowSummaryConfirm(false);
    setGeneratingSummary(true);
    setGenerateError(null);

    try {
      const requestBody: any = {
        model: selectedModel
      };

      if (summaryPrompt !== defaultSummaryPrompt) {
        requestBody.custom_prompt = summaryPrompt;
      }

      const response = await apiClient.post(`/api/lessons/${lessonId}/generate_summary/`, requestBody);

      if (response.data.success) {
        setLesson(prev => prev ? { ...prev, summary: response.data.summary } : null);
        alert('Summary generated successfully!');
      } else {
        setGenerateError(response.data.error || 'Failed to generate summary');
      }
    } catch (err: any) {
      console.error('Error generating summary:', err);
      const errorMessage = err.response?.data?.error || 'Failed to generate summary. Please try again.';
      setGenerateError(errorMessage);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleGenerateQuiz = async () => {
    if (!lesson?.transcript || !lesson.transcript.trim()) {
      setGenerateError('Cannot generate quiz: lesson has no transcript. Please transcribe the lesson first.');
      return;
    }

    setShowQuizConfirm(true);
  };

  const handleQuizAnswerChange = (questionId: string, answer: string) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmitQuiz = () => {
    if (!lesson?.quiz || typeof lesson.quiz !== 'object') return;

    let correct = 0;

    if (lesson.quiz.multipleChoice) {
      lesson.quiz.multipleChoice.forEach((question: any) => {
        if (quizAnswers[`mc-${question.id}`] === question.correctAnswer) {
          correct++;
        }
      });
    }

    setQuizScore({ correct, total: lesson.quiz.multipleChoice?.length || 0 });
    setQuizSubmitted(true);
  };

  const handleResetQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
  };

  const confirmGenerateQuiz = async () => {
    setShowQuizConfirm(false);
    setGeneratingQuiz(true);
    setGenerateError(null);

    try {
      const requestBody: any = {
        use_summary: true,
        model: selectedModel
      };

      if (quizPrompt !== defaultQuizPrompt) {
        requestBody.custom_prompt = quizPrompt;
      }

      const response = await apiClient.post(`/api/lessons/${lessonId}/generate_quiz/`, requestBody);

      if (response.data.success) {
        setLesson(prev => prev ? { ...prev, quiz: response.data.quiz } : null);
        alert('Quiz generated successfully!');
      } else {
        setGenerateError(response.data.error || 'Failed to generate quiz');
      }
    } catch (err: any) {
      console.error('Error generating quiz:', err);
      const errorMessage = err.response?.data?.error || 'Failed to generate quiz. Please try again.';
      setGenerateError(errorMessage);
    } finally {
      setGeneratingQuiz(false);
    }
  };

  if (loading) {
    return (
      <Screen>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACING[64] }}>
          <Spinner size="lg" label="Loading lesson..." />
        </div>
      </Screen>
    );
  }

  if (error || !lesson) {
    return (
      <Screen>
        <Alert variant="error">{error || 'Lesson not found'}</Alert>
      </Screen>
    );
  }

  return (
    <Screen>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: SPACING[32] }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[24] }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
              <Button variant="secondary" onClick={() => navigate(-1)} icon={<ArrowLeftIcon style={{ width: '16px', height: '16px' }} />}>
                Back
              </Button>
              <h1 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h1,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
              }}>
                {lesson.title}
              </h1>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
              <Button variant="secondary" onClick={handleEdit} icon={<PencilIcon style={{ width: '16px', height: '16px' }} />}>
                Edit
              </Button>
              <Button variant="danger" onClick={handleDelete} icon={<TrashIcon style={{ width: '16px', height: '16px' }} />}>
                Delete
              </Button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: SPACING[16] }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[24] }}>
              <div style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>
                Created: {new Date(lesson.created_at).toLocaleDateString()}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                <span style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>Status:</span>
                <Badge color={lesson.is_completed ? 'success' : 'warning'}>
                  {lesson.is_completed ? 'Completed' : 'In Progress'}
                </Badge>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                <span style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>AI Model:</span>
                <Select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  options={availableModels.map(model => ({
                    value: model.id,
                    label: `[${model.provider === 'anthropic' ? 'Claude' : 'OpenAI'}] ${model.name}`
                  }))}
                />
              </div>
            </div>
            <Button
              variant={lesson.is_completed ? 'secondary' : 'primary'}
              onClick={handleMarkCompleted}
              icon={<CheckCircleIcon style={{ width: '20px', height: '20px' }} />}
            >
              {lesson.is_completed ? 'Mark as Incomplete' : 'Mark as Complete'}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
          {/* Video Section */}
          {(lesson.video_url || lesson.video_url_signed) && (
            <Panel>
              <h2 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[16],
              }}>
                Video
              </h2>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'relative', paddingTop: '56.25%', backgroundColor: COLORS.bg.base, borderRadius: RADIUS, overflow: 'hidden' }}>
                  <video
                    controls
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                    src={lesson.video_url_signed || lesson.video_url}
                    onError={(e) => {
                      console.error('Video loading error:', e);
                      setVideoError('Failed to load video. Please try again later.');
                    }}
                    controlsList="nodownload"
                    playsInline
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
                {videoError && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(249, 250, 251, 0.95)',
                    borderRadius: RADIUS,
                  }}>
                    <div style={{ textAlign: 'center', padding: SPACING[16] }}>
                      <p style={{ color: COLORS.status.error, marginBottom: SPACING[8], fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.default }}>
                        {videoError}
                      </p>
                      <Button
                        variant="primary"
                        onClick={() => {
                          setVideoError(null);
                          const video = document.querySelector('video');
                          if (video) {
                            video.load();
                          }
                        }}
                      >
                        Try Again
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {(lesson.video_url || lesson.video_url_signed) && (
                <div style={{ marginTop: SPACING[8], fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>
                  Using {lesson.video_url_signed ? 'signed S3 URL' : 'direct URL'}
                </div>
              )}
            </Panel>
          )}

          {/* Transcript Section */}
          {lesson.transcript && (
            <Panel>
              <h2 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[16],
              }}>
                Transcript
              </h2>
              <pre style={{
                whiteSpace: 'pre-wrap',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                margin: 0,
                lineHeight: 1.6,
              }}>
                {lesson.transcript}
              </pre>
            </Panel>
          )}

          {/* Summary Section */}
          <Panel>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[16] }}>
              <h2 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
              }}>
                Summary
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                <Button
                  variant="secondary"
                  onClick={() => setShowSummaryPrompt(!showSummaryPrompt)}
                  icon={<PencilIcon style={{ width: '16px', height: '16px' }} />}
                >
                  {showSummaryPrompt ? 'Hide' : 'Edit'} Prompt
                </Button>
                <Button
                  variant="primary"
                  onClick={handleGenerateSummary}
                  disabled={generatingSummary || !lesson.transcript}
                  icon={generatingSummary ? null : <SparklesIcon style={{ width: '16px', height: '16px' }} />}
                >
                  {generatingSummary ? (
                    <>
                      <Spinner size="sm" />
                      Generating...
                    </>
                  ) : (
                    'Generate'
                  )}
                </Button>
              </div>
            </div>

            {/* Custom Prompt Editor */}
            {showSummaryPrompt && (
              <Panel style={{ marginBottom: SPACING[16], backgroundColor: COLORS.bg.base }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[8] }}>
                  <label style={{
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    fontWeight: '500',
                    color: COLORS.text.primary,
                  }}>
                    Custom Prompt
                  </label>
                  <button
                    onClick={() => setSummaryPrompt(defaultSummaryPrompt)}
                    style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.accent.default,
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Reset to Default
                  </button>
                </div>
                <Textarea
                  value={summaryPrompt}
                  onChange={(e) => setSummaryPrompt(e.target.value)}
                  rows={10}
                />
                <p style={{
                  marginTop: SPACING[8],
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  color: COLORS.text.secondary,
                  margin: `${SPACING[8]} 0 0 0`,
                }}>
                  Use <code style={{ backgroundColor: COLORS.bg.hover, padding: '2px 6px', borderRadius: RADIUS }}>&#123;transcript&#125;</code> as a placeholder for the lesson transcript.
                </p>
              </Panel>
            )}

            {lesson.summary ? (
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                whiteSpace: 'pre-wrap',
                margin: 0,
                lineHeight: 1.6,
              }}>
                {lesson.summary}
              </p>
            ) : (
              <div style={{ textAlign: 'center', padding: `${SPACING[32]} 0`, color: COLORS.text.tertiary }}>
                <ChatBubbleLeftRightIcon style={{ width: '48px', height: '48px', margin: '0 auto', marginBottom: SPACING[12], color: COLORS.text.tertiary }} />
                <p style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.default, margin: 0 }}>No summary available yet.</p>
                {lesson.transcript && (
                  <p style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small, marginTop: SPACING[8], margin: `${SPACING[8]} 0 0 0` }}>
                    Click "Generate" to create one with AI.
                  </p>
                )}
              </div>
            )}
          </Panel>

          {/* Personal Notes Section */}
          {lesson.personal_notes && (
            <Panel>
              <h2 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[16],
              }}>
                Personal Notes
              </h2>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                margin: 0,
                lineHeight: 1.6,
              }}>
                {lesson.personal_notes}
              </p>
            </Panel>
          )}

          {/* Quiz Section */}
          <Panel>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[16] }}>
              <h2 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
              }}>
                Quiz
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                <Button
                  variant="secondary"
                  onClick={() => setShowQuizPrompt(!showQuizPrompt)}
                  icon={<PencilIcon style={{ width: '16px', height: '16px' }} />}
                >
                  {showQuizPrompt ? 'Hide' : 'Edit'} Prompt
                </Button>
                <Button
                  variant="primary"
                  onClick={handleGenerateQuiz}
                  disabled={generatingQuiz || !lesson.transcript}
                  icon={generatingQuiz ? null : <QuestionMarkCircleIcon style={{ width: '16px', height: '16px' }} />}
                >
                  {generatingQuiz ? (
                    <>
                      <Spinner size="sm" />
                      Generating...
                    </>
                  ) : (
                    'Generate'
                  )}
                </Button>
              </div>
            </div>

            {/* Custom Prompt Editor */}
            {showQuizPrompt && (
              <Panel style={{ marginBottom: SPACING[16], backgroundColor: COLORS.bg.base }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[8] }}>
                  <label style={{
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    fontWeight: '500',
                    color: COLORS.text.primary,
                  }}>
                    Custom Prompt
                  </label>
                  <button
                    onClick={() => setQuizPrompt(defaultQuizPrompt)}
                    style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.accent.default,
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Reset to Default
                  </button>
                </div>
                <Textarea
                  value={quizPrompt}
                  onChange={(e) => setQuizPrompt(e.target.value)}
                  rows={20}
                />
                <p style={{
                  marginTop: SPACING[8],
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  color: COLORS.text.secondary,
                  margin: `${SPACING[8]} 0 0 0`,
                }}>
                  Use <code style={{ backgroundColor: COLORS.bg.hover, padding: '2px 6px', borderRadius: RADIUS }}>&#123;transcript&#125;</code> and <code style={{ backgroundColor: COLORS.bg.hover, padding: '2px 6px', borderRadius: RADIUS }}>&#123;summary_section&#125;</code> as placeholders.
                </p>
              </Panel>
            )}

            {lesson.quiz && typeof lesson.quiz === 'object' && Object.keys(lesson.quiz).length > 0 ? (
              <div>
                {/* Quiz Score Display */}
                {quizSubmitted && quizScore && (
                  <Panel style={{ marginBottom: SPACING[24], backgroundColor: 'rgba(59, 130, 246, 0.15)', borderColor: COLORS.accent.default }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <h3 style={{
                          fontFamily: TYPOGRAPHY.headings.fontFamily,
                          fontSize: TYPOGRAPHY.headings.sizes.h3,
                          fontWeight: TYPOGRAPHY.headings.weight,
                          color: COLORS.accent.default,
                          margin: 0,
                          marginBottom: SPACING[8],
                        }}>
                          Quiz Results
                        </h3>
                        <p style={{
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.default,
                          color: COLORS.accent.default,
                          margin: 0,
                        }}>
                          You scored <span style={{ fontWeight: '700', fontSize: TYPOGRAPHY.size['4xl'] }}>{quizScore.correct}</span> out of <span style={{ fontWeight: '700', fontSize: TYPOGRAPHY.size['4xl'] }}>{quizScore.total}</span> multiple choice questions correct
                          <span style={{ marginLeft: SPACING[8] }}>({Math.round((quizScore.correct / quizScore.total) * 100)}%)</span>
                        </p>
                      </div>
                      <Button variant="primary" onClick={handleResetQuiz}>
                        Retake Quiz
                      </Button>
                    </div>
                  </Panel>
                )}

                {/* Quiz Title */}
                {lesson.quiz.title && (
                  <h3 style={{
                    fontFamily: TYPOGRAPHY.headings.fontFamily,
                    fontSize: TYPOGRAPHY.headings.sizes.h3,
                    fontWeight: TYPOGRAPHY.headings.weight,
                    color: COLORS.text.primary,
                    margin: 0,
                    marginBottom: SPACING[16],
                  }}>
                    {lesson.quiz.title}
                  </h3>
                )}

                {/* Multiple Choice Questions */}
                {lesson.quiz.multipleChoice && lesson.quiz.multipleChoice.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24], marginBottom: SPACING[32] }}>
                    <h4 style={{
                      fontFamily: TYPOGRAPHY.headings.fontFamily,
                      fontSize: TYPOGRAPHY.headings.sizes.h4,
                      fontWeight: TYPOGRAPHY.headings.weight,
                      color: COLORS.text.primary,
                      margin: 0,
                      paddingBottom: SPACING[8],
                      borderBottom: `1px solid ${COLORS.border.default}`,
                    }}>
                      Multiple Choice Questions
                    </h4>
                    {lesson.quiz.multipleChoice.map((question: any, index: number) => {
                      const questionId = `mc-${question.id}`;
                      const userAnswer = quizAnswers[questionId];
                      const isCorrect = userAnswer === question.correctAnswer;

                      return (
                        <Panel key={question.id} style={{ backgroundColor: COLORS.bg.base }}>
                          <p style={{
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: '500',
                            color: COLORS.text.primary,
                            margin: 0,
                            marginBottom: SPACING[12],
                          }}>
                            {index + 1}. {question.question}
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
                            {question.options.map((option: any) => {
                              const isSelected = userAnswer === option.label;
                              const isCorrectOption = option.label === question.correctAnswer;
                              const showCorrect = quizSubmitted && isCorrectOption;
                              const showIncorrect = quizSubmitted && isSelected && !isCorrect;

                              return (
                                <label
                                  key={option.label}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: SPACING[12],
                                    border: `1px solid ${
                                      showCorrect ? COLORS.status.success :
                                      showIncorrect ? COLORS.status.error :
                                      isSelected ? COLORS.accent.default :
                                      COLORS.border.default
                                    }`,
                                    borderRadius: RADIUS,
                                    backgroundColor: showCorrect ? 'rgba(16, 185, 129, 0.1)' :
                                      showIncorrect ? 'rgba(239, 68, 68, 0.1)' :
                                      isSelected ? 'rgba(59, 130, 246, 0.1)' :
                                      COLORS.bg.panel,
                                    cursor: quizSubmitted ? 'default' : 'pointer',
                                    transition: 'all 0.1s ease',
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name={questionId}
                                    value={option.label}
                                    checked={isSelected}
                                    onChange={(e) => handleQuizAnswerChange(questionId, e.target.value)}
                                    disabled={quizSubmitted}
                                    style={{ marginRight: SPACING[12] }}
                                  />
                                  <span style={{
                                    fontFamily: TYPOGRAPHY.body.fontFamily,
                                    fontSize: TYPOGRAPHY.body.sizes.default,
                                    color: COLORS.text.primary,
                                  }}>
                                    <span style={{ fontWeight: '500' }}>{option.label})</span> {option.text}
                                  </span>
                                  {showCorrect && (
                                    <CheckCircleIcon style={{ width: '20px', height: '20px', color: COLORS.status.success, marginLeft: 'auto' }} />
                                  )}
                                  {showIncorrect && (
                                    <XCircleIcon style={{ width: '20px', height: '20px', color: COLORS.status.error, marginLeft: 'auto' }} />
                                  )}
                                </label>
                              );
                            })}
                          </div>
                          {quizSubmitted && question.explanation && (
                            <Panel style={{ marginTop: SPACING[12], backgroundColor: 'rgba(59, 130, 246, 0.1)', borderColor: COLORS.accent.default }}>
                              <p style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                color: COLORS.accent.default,
                                margin: 0,
                              }}>
                                <span style={{ fontWeight: '600' }}>Explanation:</span> {question.explanation}
                              </p>
                            </Panel>
                          )}
                        </Panel>
                      );
                    })}
                  </div>
                )}

                {/* Short Answer Questions */}
                {lesson.quiz.shortAnswer && lesson.quiz.shortAnswer.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24], marginBottom: SPACING[32] }}>
                    <h4 style={{
                      fontFamily: TYPOGRAPHY.headings.fontFamily,
                      fontSize: TYPOGRAPHY.headings.sizes.h4,
                      fontWeight: TYPOGRAPHY.headings.weight,
                      color: COLORS.text.primary,
                      margin: 0,
                      paddingBottom: SPACING[8],
                      borderBottom: `1px solid ${COLORS.border.default}`,
                    }}>
                      Short Answer Questions
                    </h4>
                    {lesson.quiz.shortAnswer.map((question: any, index: number) => {
                      const questionId = `sa-${question.id}`;
                      const userAnswer = quizAnswers[questionId];

                      return (
                        <Panel key={question.id} style={{ backgroundColor: COLORS.bg.base }}>
                          <p style={{
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: '500',
                            color: COLORS.text.primary,
                            margin: 0,
                            marginBottom: SPACING[12],
                          }}>
                            {typeof lesson.quiz === 'object' && lesson.quiz?.multipleChoice ? lesson.quiz.multipleChoice.length + index + 1 : index + 1}. {question.question}
                          </p>
                          <textarea
                            value={userAnswer || ''}
                            onChange={(e) => handleQuizAnswerChange(questionId, e.target.value)}
                            disabled={quizSubmitted}
                            rows={4}
                            placeholder="Type your answer here..."
                            style={{
                              width: '100%',
                              padding: SPACING[12],
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              color: COLORS.text.primary,
                              backgroundColor: quizSubmitted ? COLORS.bg.hover : COLORS.bg.base,
                              border: `1px solid ${COLORS.border.default}`,
                              borderRadius: RADIUS,
                              outline: 'none',
                              resize: 'vertical',
                              cursor: quizSubmitted ? 'not-allowed' : 'text',
                            }}
                          />
                          {quizSubmitted && question.sampleAnswer && (
                            <Panel style={{ marginTop: SPACING[12], backgroundColor: 'rgba(16, 185, 129, 0.1)', borderColor: COLORS.status.success }}>
                              <p style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                color: COLORS.status.success,
                                margin: 0,
                              }}>
                                <span style={{ fontWeight: '600' }}>Sample Answer:</span> {question.sampleAnswer}
                              </p>
                            </Panel>
                          )}
                        </Panel>
                      );
                    })}
                  </div>
                )}

                {/* Submit Button */}
                {!quizSubmitted && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="primary" onClick={handleSubmitQuiz}>
                      Submit Quiz
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: `${SPACING[32]} 0`, color: COLORS.text.tertiary }}>
                <QuestionMarkCircleIcon style={{ width: '48px', height: '48px', margin: '0 auto', marginBottom: SPACING[12], color: COLORS.text.tertiary }} />
                <p style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.default, margin: 0 }}>No quiz available yet.</p>
                {lesson.transcript && (
                  <p style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small, marginTop: SPACING[8], margin: `${SPACING[8]} 0 0 0` }}>
                    Click "Generate" to create one with AI.
                  </p>
                )}
              </div>
            )}
          </Panel>

          {/* Generate Error Display */}
          {generateError && (
            <Alert variant="error" onClose={() => setGenerateError(null)}>
              {generateError}
            </Alert>
          )}
        </div>
      </div>

      <EditLessonForm
        isOpen={isEditFormOpen}
        onClose={() => {
          setIsEditFormOpen(false);
          if (isEditMode) {
            navigate(`/lessons/${lessonId}`);
          }
        }}
        onLessonUpdated={handleLessonUpdated}
        lessonId={Number(lessonId)}
      />

      {/* Summary Generation Confirmation Modal */}
      {showSummaryConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={() => setShowSummaryConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: COLORS.bg.panel,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              maxWidth: '500px',
              width: '100%',
              margin: SPACING[16],
              overflow: 'hidden',
            }}
          >
            <div style={{
              padding: SPACING[24],
              backgroundColor: 'rgba(16, 185, 129, 0.15)',
              borderBottom: `1px solid ${COLORS.status.success}`,
              display: 'flex',
              alignItems: 'center',
            }}>
              <SparklesIcon style={{ width: '24px', height: '24px', color: COLORS.status.success, marginRight: SPACING[12] }} />
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
              }}>
                Generate Summary
              </h3>
            </div>
            <div style={{ padding: SPACING[24] }}>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[8],
              }}>
                Generate an AI-powered summary for this lesson using <span style={{ fontWeight: '500' }}>{availableModels.find(m => m.id === selectedModel)?.name || selectedModel}</span>?
              </p>
              {lesson?.summary && (
                <div style={{ marginTop: SPACING[12] }}>
                  <Alert variant="warning">
                    This will replace the existing summary.
                  </Alert>
                </div>
              )}
            </div>
            <div style={{
              padding: SPACING[24],
              backgroundColor: COLORS.bg.base,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: SPACING[12],
            }}>
              <Button variant="secondary" onClick={() => setShowSummaryConfirm(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={confirmGenerateSummary}>
                Generate Summary
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Generation Confirmation Modal */}
      {showQuizConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={() => setShowQuizConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: COLORS.bg.panel,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              maxWidth: '500px',
              width: '100%',
              margin: SPACING[16],
              overflow: 'hidden',
            }}
          >
            <div style={{
              padding: SPACING[24],
              backgroundColor: 'rgba(245, 158, 11, 0.15)',
              borderBottom: `1px solid ${COLORS.status.warning}`,
              display: 'flex',
              alignItems: 'center',
            }}>
              <QuestionMarkCircleIcon style={{ width: '24px', height: '24px', color: COLORS.status.warning, marginRight: SPACING[12] }} />
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
              }}>
                Generate Quiz
              </h3>
            </div>
            <div style={{ padding: SPACING[24] }}>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[8],
              }}>
                Generate an AI-powered quiz for this lesson using <span style={{ fontWeight: '500' }}>{availableModels.find(m => m.id === selectedModel)?.name || selectedModel}</span>?
              </p>
              {lesson?.quiz && (
                <div style={{ marginTop: SPACING[12] }}>
                  <Alert variant="warning">
                    This will replace the existing quiz.
                  </Alert>
                </div>
              )}
            </div>
            <div style={{
              padding: SPACING[24],
              backgroundColor: COLORS.bg.base,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: SPACING[12],
            }}>
              <Button variant="secondary" onClick={() => setShowQuizConfirm(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={confirmGenerateQuiz}>
                Generate Quiz
              </Button>
            </div>
          </div>
        </div>
      )}
    </Screen>
  );
};

export default LessonDetailPage;
